import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { useCartAdd } from 'src/core/state/cart';
import { DataStatus } from 'src/core/engine';
import { StockStatus } from 'src/types/global-types';

import { selectVariantFromOptions, appendStockToSize } from '../ProductDetail/utils/selectVariantsFromOptions';
import { MessagesModal } from '../Modal/MessagesModal';
import { Selector } from '../Selector/Selector';
import { Size } from '../ProductDetail/ProductSizes';
import { notCustomizableSizesList, sizeSelectItems, Swatch } from '../ProductDetail/ProductSwatch';
import { colourSelectItems } from '../ProductDetail/ColourPicker';
import { dropdownSwatchesList } from '../ProductDetail/ColourPicker';
import { Button } from '../Button/Button';
import { Variant } from '../CategoryListingPage/utils/productListItem';

import classes from './UpsellItem.scss';

type Props = {
    sizes: Size[];
    swatches: Swatch[];
    parentSku: string;
    variants: Variant[];
    initialColorId?: number;
    onClose?: () => void;
    setPending: (pending: boolean) => void;
};

function attributeValue(variant: Variant, code: string): number {
    const attr = variant.attributes.find((item) => item.code === code);
    return Number(attr?.valueIndex || attr?.value_index || 0);
}

function productStock(product: Variant['product'] | undefined) {
    const status = product?.stock_status ?? product?.stockStatus;
    const qty = product?.stock_qty ?? product?.stockQty ?? null;
    return { status, qty: qty == null ? null : Number(qty) };
}

function normalizeVariant(variant: Variant) {
    if (!variant.product) {
        return variant as any;
    }

    const { status, qty } = productStock(variant.product);
    const stock_status =
        status === 'OUT_OF_STOCK' || status === StockStatus.OutOfStock ? StockStatus.OutOfStock : StockStatus.InStock;

    return {
        sku: variant.product.sku,
        id: variant.product.id,
        attributes: variant.attributes.map((attr) => ({
            code: attr.code,
            label: attr.label,
            value_index: attr.valueIndex || attr.value_index,
        })),
        size_id: attributeValue(variant, 'size'),
        color_id: attributeValue(variant, 'color'),
        stock_status,
        max_qty: qty,
        product: {
            sku: variant.product.sku,
            id: variant.product.id,
            stock_status,
            stock_qty: qty,
        },
    };
}

function isOutOfStock(size?: Size): boolean {
    if (!size) return false;
    return size.stock === StockStatus.OutOfStock || size.maxQty === 0;
}

function sizesForColor(allSizes: Size[], color: number, variants: Variant[]): Size[] {
    if (!color) return [];
    return allSizes
        .map((size) => appendStockToSize(size, { color }, variants as any))
        .filter((size) => Boolean(size.simpleProductSku));
}

function firstInStockSize(sizes: Size[]): Size | undefined {
    return sizes.find((size) => !isOutOfStock(size)) || sizes[0];
}

function firstColorWithSizes(swatches: Swatch[], allSizes: Size[], variants: Variant[], preferred?: number): number {
    const candidates = [preferred, ...swatches.map((swatch) => Number(swatch.value_index))].filter(
        (id): id is number => Boolean(id) && !Number.isNaN(id),
    );

    return (
        candidates.find((id) => sizesForColor(allSizes, id, variants).some((size) => !isOutOfStock(size))) ||
        candidates.find((id) => sizesForColor(allSizes, id, variants).length > 0) ||
        candidates[0] ||
        0
    );
}

export const UpsellActions: React.FC<Props> = ({
    sizes,
    swatches,
    parentSku,
    variants,
    initialColorId,
    onClose,
    setPending,
}) => {
    const normalizedVariants = useMemo(() => variants.map(normalizeVariant), [variants]);
    const initialColorState = firstColorWithSizes(swatches, sizes, normalizedVariants, initialColorId);
    const initialSizeState =
        firstInStockSize(sizesForColor(sizes, Number(initialColorState), normalizedVariants))?.id || '';
    const initialQuantity = 1;
    const submittedRef = useRef(false);
    const submitLockedRef = useRef(false);
    const unlockSubmitTimerRef = useRef<number | null>(null);
    const [submitLocked, setSubmitLocked] = useState(false);

    const lockSubmitAfterDropdown = useCallback(() => {
        submitLockedRef.current = true;
        setSubmitLocked(true);
        if (unlockSubmitTimerRef.current !== null) {
            window.clearTimeout(unlockSubmitTimerRef.current);
        }
        unlockSubmitTimerRef.current = window.setTimeout(() => {
            submitLockedRef.current = false;
            setSubmitLocked(false);
            unlockSubmitTimerRef.current = null;
        }, 300);
    }, []);

    const [openDropdown, setOpenDropdown] = useState<'color' | 'size' | null>(null);

    const handleDropdownOpenChange = useCallback(
        (field: 'color' | 'size') => (open: boolean) => {
            setOpenDropdown(open ? field : null);
            if (!open) {
                lockSubmitAfterDropdown();
            }
        },
        [lockSubmitAfterDropdown],
    );

    useEffect(() => {
        return () => {
            if (unlockSubmitTimerRef.current !== null) {
                window.clearTimeout(unlockSubmitTimerRef.current);
            }
        };
    }, []);

    const { addConfig, messages, status, clearMessages } = useCartAdd();
    const pending = status === DataStatus.Pending;

    useEffect(() => {
        setPending(pending);
    }, [pending, setPending]);

    useEffect(() => {
        if (!submittedRef.current) return;
        if (status === DataStatus.Success) {
            submittedRef.current = false;
            onClose?.();
        }
        if (status === DataStatus.Error) {
            submittedRef.current = false;
        }
    }, [status, onClose]);

    const formik = useFormik({
        initialValues: {
            color: initialColorState,
            size: initialSizeState,
            quantity: initialQuantity,
        },
        onSubmit: (values) => {
            if (submitLockedRef.current) {
                return;
            }

            const { color, size, quantity } = values;

            if (!color || !size || quantity <= 0) {
                return;
            }

            const options = {
                color: Number(color),
                size: Number(size),
            };

            const variant = selectVariantFromOptions(options, normalizedVariants as any);
            const selectedSize = sizesForColor(sizes, Number(color), normalizedVariants).find(
                (item) => Number(item.id) === Number(size),
            );

            if (!variant || !variant.sku || isOutOfStock(selectedSize)) {
                return;
            }

            if (selectedSize?.maxQty != null && quantity > selectedSize.maxQty) {
                return;
            }

            const items = [
                {
                    data: {
                        quantity: Number(quantity),
                        sku: String(variant.sku),
                        tax_free: false,
                    },
                    parent_sku: String(parentSku),
                    variant_sku: String(variant.sku),
                },
            ];

            submittedRef.current = true;
            addConfig({ items });
        },
    });

    const availableSizes = sizesForColor(sizes, Number(formik.values.color), normalizedVariants);
    const selectedSize = availableSizes.find((size) => Number(size.id) === Number(formik.values.size));
    const maxQty = selectedSize?.maxQty ?? null;
    const selectedOutOfStock = isOutOfStock(selectedSize);
    const exceedsStock = maxQty != null && formik.values.quantity > maxQty;

    const handleChangeSizeState = useCallback(
        (value) => {
            lockSubmitAfterDropdown();
            const nextSize = availableSizes.find((size) => Number(size.id) === Number(value));
            formik.setFieldValue('size', value);
            if (nextSize?.maxQty != null && formik.values.quantity > nextSize.maxQty) {
                formik.setFieldValue('quantity', nextSize.maxQty);
            }
        },
        [availableSizes, formik, lockSubmitAfterDropdown],
    );

    const handleChangeColorState = useCallback(
        (value) => {
            const color = Number(value);
            lockSubmitAfterDropdown();
            const nextSizes = sizesForColor(sizes, color, normalizedVariants);
            const nextSize = firstInStockSize(nextSizes);
            formik.setFieldValue('color', color);
            formik.setFieldValue('size', nextSize?.id || '');
            if (nextSize?.maxQty != null && formik.values.quantity > nextSize.maxQty) {
                formik.setFieldValue('quantity', nextSize.maxQty);
            }
        },
        [formik, lockSubmitAfterDropdown, sizes, normalizedVariants],
    );

    const handleQuantityChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = parseInt(e.target.value, 10) || 0;
            formik.setFieldValue('quantity', Math.max(0, value));
        },
        [formik],
    );

    const handleQuantityIncrease = useCallback(() => {
        const next = (formik.values.quantity || 0) + 1;
        if (maxQty != null && next > maxQty) {
            formik.setFieldValue('quantity', maxQty);
            return;
        }
        formik.setFieldValue('quantity', next);
    }, [formik, maxQty]);

    const handleQuantityDecrease = useCallback(() => {
        if (formik.values.quantity > 0) {
            formik.setFieldValue('quantity', formik.values.quantity - 1);
        }
    }, [formik]);

    const sizeSelectItemsComponent = sizeSelectItems(availableSizes, formik.values.size, handleChangeSizeState);
    const notCustomizableSizesListComponent = notCustomizableSizesList(availableSizes);

    const colourSelectItemsComponent = colourSelectItems(swatches, formik.values.color, handleChangeColorState);
    const dropdownSwatchesListComponent = dropdownSwatchesList(swatches);

    const isDisabled =
        pending ||
        submitLocked ||
        formik.values.quantity <= 0 ||
        !formik.values.color ||
        !formik.values.size ||
        availableSizes.length === 0 ||
        selectedOutOfStock ||
        exceedsStock;

    return (
        <form
            onSubmit={(e) => {
                if (submitLockedRef.current) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
                formik.handleSubmit(e);
            }}
            className={classes.upsellActionsForm}
        >
            <div className={classes.upsellActionsWrapper}>
                <div
                    className={classnames(classes.colourSelector, { [classes.openDropdown]: openDropdown === 'color' })}
                >
                    <Selector
                        selectItems={colourSelectItemsComponent}
                        selectOptions={dropdownSwatchesListComponent}
                        handleChangeState={handleChangeColorState}
                        onOpenChange={handleDropdownOpenChange('color')}
                        initialState={formik.values.color}
                        field="color"
                        short
                        accessibleName="Choose colour"
                    />
                </div>
                <div className={classnames(classes.sizeSelector, { [classes.openDropdown]: openDropdown === 'size' })}>
                    <Selector
                        key={`size-${formik.values.color}`}
                        selectItems={sizeSelectItemsComponent}
                        selectOptions={notCustomizableSizesListComponent}
                        handleChangeState={handleChangeSizeState}
                        onOpenChange={handleDropdownOpenChange('size')}
                        initialState={formik.values.size}
                        field="size"
                        short
                        accessibleName="Choose size"
                    />
                </div>
                <div className={classes.quantitySelector}>
                    <div className={classes.quantityControls}>
                        <button
                            type="button"
                            onClick={handleQuantityDecrease}
                            className={classes.quantityButton}
                            disabled={formik.values.quantity <= 0}
                        >
                            -
                        </button>
                        <input
                            type="number"
                            name="quantity"
                            value={formik.values.quantity}
                            onChange={handleQuantityChange}
                            className={classes.quantityInput}
                            min="0"
                            max={maxQty ?? undefined}
                        />
                        <button
                            type="button"
                            onClick={handleQuantityIncrease}
                            className={classes.quantityButton}
                            disabled={selectedOutOfStock || (maxQty != null && formik.values.quantity >= maxQty)}
                        >
                            +
                        </button>
                    </div>
                    {selectedOutOfStock && (
                        <p className={classes.stockMessage}>Out of stock. Please choose a different size.</p>
                    )}
                    {!selectedOutOfStock && exceedsStock && maxQty != null && (
                        <p className={classes.stockMessage}>Only {maxQty} available. Please reduce the quantity.</p>
                    )}
                    {!selectedOutOfStock && !exceedsStock && maxQty != null && maxQty > 0 && maxQty < 100 && (
                        <p className={classes.stockHint}>{maxQty} available</p>
                    )}
                </div>
            </div>
            <div className={classes.upsellActionsSubmit}>
                <Button
                    type="submit"
                    className={classes.addToBasketButton}
                    disabled={isDisabled}
                    onClick={(e) => {
                        if (submitLockedRef.current) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                >
                    {pending ? 'Please wait..' : 'Add to basket'}
                </Button>
            </div>
            {messages.length > 0 && (
                <MessagesModal messages={messages} close={clearMessages} title="An error occurred" />
            )}
        </form>
    );
};
